import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';

const SubscriptionContext = createContext();

// Action types
const SUBSCRIPTION_ACTIONS = {
  SET_STATUS: 'SET_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  UPDATE_USAGE: 'UPDATE_USAGE',
  REFRESH: 'REFRESH'
};

// Initial state
const initialState = {
  status: null,
  loading: true,
  error: null,
  lastUpdated: null
};

// Reducer
const subscriptionReducer = (state, action) => {
  switch (action.type) {
    case SUBSCRIPTION_ACTIONS.SET_STATUS:
      return {
        ...state,
        status: action.payload,
        loading: false,
        error: null,
        lastUpdated: new Date()
      };
    
    case SUBSCRIPTION_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case SUBSCRIPTION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case SUBSCRIPTION_ACTIONS.UPDATE_USAGE:
      return {
        ...state,
        status: {
          ...state.status,
          usage: {
            ...state.status?.usage,
            ...action.payload
          }
        }
      };
    
    case SUBSCRIPTION_ACTIONS.REFRESH:
      return {
        ...state,
        loading: true
      };
    
    default:
      return state;
  }
};

// Provider component
export const SubscriptionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(subscriptionReducer, initialState);
  const subscriptionHook = useSubscription();

  // Sync hook state with context
  useEffect(() => {
    if (subscriptionHook.subscriptionStatus) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.SET_STATUS,
        payload: subscriptionHook.subscriptionStatus
      });
    }
  }, [subscriptionHook.subscriptionStatus]);

  useEffect(() => {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.SET_LOADING,
      payload: subscriptionHook.loading
    });
  }, [subscriptionHook.loading]);

  useEffect(() => {
    if (subscriptionHook.error) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.SET_ERROR,
        payload: subscriptionHook.error
      });
    }
  }, [subscriptionHook.error]);

  // Context value
  const value = {
    // State
    ...state,
    
    // Actions
    refresh: () => {
      dispatch({ type: SUBSCRIPTION_ACTIONS.REFRESH });
      subscriptionHook.refreshSubscriptionStatus();
    },
    
    updateUsage: (featureName, usage) => {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.UPDATE_USAGE,
        payload: { [featureName]: usage }
      });
    },
    
    // Computed values
    hasActiveSubscription: () => {
      return state.status?.status?.is_active || false;
    },
    
    isOnTrial: () => {
      return state.status?.status?.is_trial || false;
    },
    
    getPlanType: () => {
      return state.status?.status?.plan || null;
    },
    
    getDaysRemaining: () => {
      return state.status?.status?.days_remaining || 0;
    },
    
    canAccessFeature: (featureName) => {
      if (!state.status) return false;
      
      const featureAccess = state.status.can_access_features?.[featureName];
      if (featureAccess !== undefined) {
        return featureAccess;
      }
      
      const usage = state.status.usage?.[featureName];
      return usage?.has_access || false;
    },
    
    getFeatureUsage: (featureName) => {
      return state.status?.usage?.[featureName] || null;
    },
    
    getRemainingQuota: (featureName) => {
      const usage = state.status?.usage?.[featureName];
      return usage?.remaining || 0;
    },
    
    getCurrentUsage: (featureName) => {
      const usage = state.status?.usage?.[featureName];
      return usage?.current_usage || 0;
    },
    
    isUsageLimitReached: (featureName) => {
      const usage = state.status?.usage?.[featureName];
      return usage && !usage.is_unlimited && usage.remaining <= 0;
    },
    
    isUsageLimitNear: (featureName, threshold = 0.8) => {
      const usage = state.status?.usage?.[featureName];
      if (!usage || usage.is_unlimited) return false;
      return (usage.current_usage / usage.limit) >= threshold;
    },
    
    getUsagePercentage: (featureName) => {
      const usage = state.status?.usage?.[featureName];
      if (!usage || usage.is_unlimited || usage.limit === 0) return 0;
      return Math.round((usage.current_usage / usage.limit) * 100);
    },
    
    // Hook methods
    incrementFeatureUsage: subscriptionHook.incrementFeatureUsage,
    checkFeatureAccess: subscriptionHook.checkFeatureAccess,
    
    // Recommendations
    getUpgradeRecommendations: () => {
      return state.status?.recommendations || [];
    }
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use subscription context
export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

// Higher-order component for subscription-aware components
export const withSubscription = (WrappedComponent, requiredFeature = null) => {
  return function SubscriptionAwareComponent(props) {
    const subscriptionContext = useSubscriptionContext();
    
    // If no specific feature is required, just pass the context
    if (!requiredFeature) {
      return <WrappedComponent {...props} subscription={subscriptionContext} />;
    }
    
    // Check if user can access the required feature
    const canAccess = subscriptionContext.canAccessFeature(requiredFeature);
    const isLimitReached = subscriptionContext.isUsageLimitReached(requiredFeature);
    
    if (!canAccess || isLimitReached) {
      return (
        <div className="subscription-required-placeholder">
          <div className="text-center p-4">
            <h5>Feature Not Available</h5>
            <p className="text-muted">
              This feature requires a higher subscription plan.
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                // Navigate to subscription page or show upgrade modal
                window.location.href = '/settings/subscription';
              }}
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} subscription={subscriptionContext} />;
  };
}; 